import React, { useRef, useCallback } from 'react'

import { i18n, useTranslation } from 'next-i18next'
import { Tooltip } from '@enuygun/design-system'
import { useDeviceType } from 'hooks/useDeviceType'

import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import { useAppState } from 'context/AppContext'

import * as S from './DomesticFlightsWingie.styled'
import { InfoCircleOutline } from '@enuygun/icons'
import { IconWrapper, TooltipText } from 'components/styled'
import sendTrackerLog from 'utils/tracker'
import { getAnalyticsRequestId } from '../../utils/analyticsRequestId'

const marketTestIdPrefix = (market?: string) => (market === 'tr' ? 'enuygun' : 'wingie')

const sliceArray = (tempArray: any[], piece: number) => {
  const newArray: any[] = []
  for (let i = 0; i < tempArray.length; i += piece) {
    //@ts-ignore
    newArray.push(tempArray?.slice(i, i + piece))
  }
  return newArray
}

const isRTL = (): boolean => {
  const lang = i18n?.language
  const dirLang = lang?.includes('-') ? lang.split('-')[1] : lang
  return i18n?.dir(dirLang) === 'rtl'
}

const DomesticFlightGroup = ({ group, language, t, index: groupIndex, market, onRouteClick }) => {
  const prefix = marketTestIdPrefix(market)
  return (
    <div>
      {group?.map((route, index) => {
        const order = groupIndex * 8 + index + 1
        const itemIndex = groupIndex * 8 + index
        return (
          <S.Ul key={route.url ?? route.cityName} dir={i18n?.dir(language)} data-testid={`${prefix}-homepage-domestic-flights-${itemIndex}-column`}>
            <li>
              <a
                href={route.url}
                title={t('travelRoutesTitle', { name: route.cityName })}
                target="_blank"
                rel="noreferrer"
                data-testid={`${prefix}-homepage-domestic-flights-${itemIndex}-link`}
                onClick={() => onRouteClick(route.url, route.cityName, order)}
              >
                {route.cityName}
              </a>
            </li>
          </S.Ul>
        )
      })}
    </div>
  )
}

interface DomesticFlightsWingieProps {
  res: any
  isTablet?: boolean
}

const DomesticFlightsWingie: React.FC<DomesticFlightsWingieProps> = ({ res, isTablet: isTabletProp }) => {
  const { language, market } = useAppState()
  const { t } = useTranslation('common')
  const { isMobile, isTablet: isTabletDevice } = useDeviceType()
  const isTablet = isTabletProp || isTabletDevice

  const domesticFlightsContainerRef = useRef<HTMLDivElement>(null)

  const tooltipText = t('DomesticFlights.headingTooltip', { defaultValue: false })
  const isWingie = market !== 'tr'
  const prefix = marketTestIdPrefix(market)
  const rtl = isRTL()

  // DomesticFlightsWingie route click tracking
  const handleRouteClick = useCallback(
    (url: string, cityName: string, order: number) => {
      const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        type: 'click',
        message: 'domestic_flights_link_click',
        environment: environment,
        requestId,
        linkUrl: url,
        cityName: cityName,
        routeOrder: order,
        isMobile: !!(isMobile || isTablet),
        market: market,
      })
    },
    [isMobile, isTablet, market, isWingie]
  )

  let data = res[3]?.domesticCities || []

  if (data.length === 0) return null

  const sliced = sliceArray(data, 8)
  const isCompact = !!(isMobile || isTablet)
  const iconBgColor = isCompact ? '#F8FAF9' : '#fff'

  const flightGroups = sliced.map((group, index) => (
    <DomesticFlightGroup
      key={group[0]?.url ?? group[0]?.cityName ?? `group-${index}`}
      group={group}
      language={language}
      t={t}
      index={index}
      market={market}
      onRouteClick={handleRouteClick}
    />
  ))

  return (
    <S.DomesticFlightsContainer ref={domesticFlightsContainerRef} isTablet={isTablet} data-testid={`${prefix}-homepage-domestic-flights-container`}>
      <S.DomesticFlightsHeader isTablet={isTablet} data-testid={`${prefix}-homepage-domestic-flights-header`}>
        {t('DomesticFlights.heading')}
        {tooltipText ? (
          <span style={{ display: 'inline-block' }} data-testid={`${prefix}-homepage-domestic-flights-header-tooltip`}>
            <Tooltip
              bottomSheet={true}
              title={<div></div>}
              popover={{ width: '350px', popover: { placement: 'bottom' } }}
              button={
                <IconWrapper bgColor={iconBgColor} data-testid={`${prefix}-homepage-domestic-flights-header-tooltip-button`}>
                  <InfoCircleOutline width={15.6} />
                </IconWrapper>
              }
            >
              <TooltipText data-testid={`${prefix}-homepage-domestic-flights-header-tooltip-text`}>{tooltipText}</TooltipText>
            </Tooltip>
          </span>
        ) : null}
      </S.DomesticFlightsHeader>
      <S.DomesticFlightsWrapper data-testid={`${prefix}-homepage-domestic-flights-wrapper`}>
        <S.MobileSliderContainer>
          {isCompact ? (
            <SnapCarousel
              options={{
                perPage: isTablet ? 3.4 : 2.4,
                gap: 16,
              }}
              containerWidth={1120}
              isMobile={!!isMobile}
              isRTL={rtl}
            >
              {flightGroups}
            </SnapCarousel>
          ) : (
            <Slider isRTL={rtl} options={{ arrows: false, pagination: true, perPage: 6, breakpoints: { 620: { perPage: 2 }, 768: { perPage: 3 }, 1240: { perPage: 4 } } }}>
              {flightGroups}
            </Slider>
          )}
        </S.MobileSliderContainer>
      </S.DomesticFlightsWrapper>
    </S.DomesticFlightsContainer>
  )
}

export default DomesticFlightsWingie
